import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  createUserWithEmailAndPassword,
  getCurrentUser,
  getCurrentUserSettings,
  loginWithEmailAndPassword,
  logoutUserWithToken,
  requestAccessTokenWithRefreshToken,
  updateUserProfile,
} from "../api/session";
import type {
  LoginResponse,
  ApiResponse,
  UserData,
  UserRoleGrant,
} from "../api/session";

export type { UserRoleGrant };

export interface User {
  id?: string;
  email?: string;
  roles?: UserRoleGrant[];
  locale?: string;
  createdAt?: string;
}

export interface Company {
  id?: string;
  name?: string;
  active_modules?: string[];
  activeTripletexIntegration?: boolean;
  hours_handler_id?: string | number;
}

export interface UserLoginData {
  email: string;
  password: string;
}

export interface UserUpdateData {
  currentPassword: string;
  token: string | undefined;
  email?: string;
  password?: string;
}

interface AuthState {
  currentUser?: User;
  currentCompany?: Company;
  loading: boolean;
  error: boolean;
  errorMessages: string[];
  accessToken?: string;
  refreshToken?: string | null;
  expiresIn?: number;
  tokenType?: string;
  currentRoute?: string;
}

const initialState: AuthState = {
  currentUser: {
    id: undefined,
    email: undefined,
    roles: undefined,
    locale: undefined,
    createdAt: undefined,
  },
  currentCompany: {
    id: undefined,
    name: undefined,
    active_modules: undefined,
    activeTripletexIntegration: undefined,
    hours_handler_id: undefined,
  },
  loading: true,
  error: false,
  errorMessages: [],
  accessToken: undefined,
  refreshToken: getRefreshToken(),
  expiresIn: undefined,
  tokenType: undefined,
};

// The function below is called a thunk and allows us to perform async logic. It
// can be dispatched like a regular action: `dispatch(incrementAsync(10))`. This
// will call the thunk with the `dispatch` function as the first argument. Async
// code can then be executed and other actions can be dispatched. Thunks are
// typically used to make async requests.
export const signUpUser = createAsyncThunk<
  ApiResponse,
  UserLoginData,
  { rejectValue: ApiResponse }
>(
  "session/signUpUser",
  async (payload: UserLoginData, { rejectWithValue }) => {
    const response = await createUserWithEmailAndPassword(
      payload.email,
      payload.password
    );
    if (response.errors) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(response);
    }

    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

type LoginResponseWithUser = LoginResponse & { company?: Company; data?: UserData };

export const updateProfile = createAsyncThunk<
  ApiResponse & { company?: Company },
  UserUpdateData,
  { rejectValue: ApiResponse }
>(
  "session/updateProfile",
  async (
    payload: UserUpdateData,
    { rejectWithValue }
  ) => {
    const response = await updateUserProfile(
      payload.currentPassword,
      payload.token,
      payload?.email,
      payload?.password
    );
    if (response.errors) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(response);
    }
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const loginUser = createAsyncThunk<
  LoginResponseWithUser,
  UserLoginData,
  { rejectValue: LoginResponse | UserData | { error: string } }
>(
  "session/loginUser",
  async (
    payload: UserLoginData,
    { rejectWithValue }
  ) => {
    const loginResponse = await loginWithEmailAndPassword(
      payload.email,
      payload.password
    );

    if (loginResponse.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(loginResponse);
    }

    if (!loginResponse.access_token) {
      return rejectWithValue({ error: "No access token received" });
    }

    const userResponse = await getCurrentUser(loginResponse.access_token);
    if (userResponse.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(userResponse.data as UserData);
    }

    const userSettingsResponse = await getCurrentUserSettings(
      loginResponse.access_token
    );

    if (userSettingsResponse.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(userSettingsResponse.data as UserData);
    }

    const response: LoginResponseWithUser = {
      ...loginResponse,
      ...userResponse?.data,
      ...userSettingsResponse?.data,
      company: userResponse?.data?.company, // Include company from userResponse
    };

    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

/** After Microsoft (or other OAuth) token exchange — load user + settings like password login. */
type OAuthTokenPayload = {
  access_token: string;
  refresh_token?: string;
  expires_in?: number;
  token_type?: string;
};

export const bootstrapSessionFromOAuthTokens = createAsyncThunk<
  LoginResponseWithUser,
  OAuthTokenPayload,
  { rejectValue: UserData | { error: string } }
>(
  "session/bootstrapSessionFromOAuthTokens",
  async (tokens, { rejectWithValue }) => {
    if (!tokens.access_token) {
      return rejectWithValue({ error: "No access token received" });
    }
    const userResponse = await getCurrentUser(tokens.access_token);
    if (userResponse.error) {
      return rejectWithValue(userResponse.data as UserData);
    }
    const userSettingsResponse = await getCurrentUserSettings(
      tokens.access_token
    );
    if (userSettingsResponse.error) {
      return rejectWithValue(userSettingsResponse.data as UserData);
    }
    const response: LoginResponseWithUser = {
      ...tokens,
      ...userResponse?.data,
      ...userSettingsResponse?.data,
      company: userResponse?.data?.company,
    };
    return response;
  }
);

export const logoutUser = createAsyncThunk<
  ApiResponse,
  string,
  { rejectValue: ApiResponse }
>(
  "session/logoutUser",
  async (payload: string, { rejectWithValue }) => {
    const response = await logoutUserWithToken(payload);
    // if response has errors rejectwithvalue
    if (response.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(response);
    }
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const logoutUserWithoutToken = createAsyncThunk(
  "session/logoutUserWithoutToken",
  async () => {
    return null;
  }
);

/**
 * Re-fetch `/users/me` + settings using the current access token (no OAuth refresh).
 * Needed after company switch and on reload: persisted Redux may hold stale `currentCompany.active_modules`.
 */
export const syncSessionFromApi = createAsyncThunk<
  LoginResponseWithUser,
  void,
  { rejectValue: { error: string }; state: { session: AuthState } }
>(
  "session/syncSessionFromApi",
  async (_, { getState, rejectWithValue }) => {
    const token = getState().session.accessToken;
    if (!token) {
      return rejectWithValue({ error: "No access token" });
    }

    const userResponse = await getCurrentUser(token);
    const unauthorized =
      userResponse.status === 401 ||
      Boolean(userResponse.error) ||
      Boolean(
        userResponse.data &&
          typeof userResponse.data === "object" &&
          "error" in userResponse.data &&
          (userResponse.data as { error?: unknown }).error
      );
    if (unauthorized) {
      return rejectWithValue({ error: "Session sync failed" });
    }

    const userSettingsResponse = await getCurrentUserSettings(token);
    if (userSettingsResponse.error && !userSettingsResponse.data) {
      return rejectWithValue({
        error: userSettingsResponse.error || "Settings sync failed",
      });
    }

    const response: LoginResponseWithUser = {
      ...(userResponse.data as object),
      ...(userSettingsResponse.data as object),
      company: userResponse?.data?.company,
    };
    return response;
  }
);

export const refreshAccessToken = createAsyncThunk<
  LoginResponseWithUser,
  string | undefined | null,
  { rejectValue: LoginResponse | UserData | string | { error: string } }
>(
  "session/refreshAccessToken",
  async (
    refreshToken: string | undefined | null,
    { rejectWithValue }
  ) => {
    if (!refreshToken) {
      return rejectWithValue("No refresh token");
    }

    const refreshResponse = await requestAccessTokenWithRefreshToken(
      refreshToken
    );

    if (refreshResponse.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(refreshResponse);
    }

    if (!refreshResponse.access_token) {
      return rejectWithValue({ error: "No access token received" });
    }

    const userResponse = await getCurrentUser(refreshResponse.access_token);

    if (userResponse.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(userResponse.data as UserData);
    }

    const userSettingsResponse = await getCurrentUserSettings(
      refreshResponse.access_token
    );

    if (userSettingsResponse.error) {
      // The value we return becomes the `rejected` action payload
      return rejectWithValue(userSettingsResponse.data as UserData);
    }

    const response: LoginResponseWithUser = {
      ...refreshResponse,
      ...userResponse?.data,
      ...userSettingsResponse?.data,
      company: userResponse?.data?.company, // Include company from userResponse
    };
    // The value we return becomes the `fulfilled` action payload
    return response;
  }
);

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setCurrentUser: (state: AuthState, action: PayloadAction<User>) => {
      state.currentUser = action.payload;
    },
    resetErrorState: (state: AuthState) => {
      state.error = false;
      state.errorMessages = [];
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(signUpUser.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(signUpUser.fulfilled, (state: AuthState, action) => {
        const payload = action.payload;
        state.accessToken = payload.access_token;
        state.refreshToken = payload.refresh_token;
        state.expiresIn = payload.expires_in;
        state.tokenType = payload.token_type;
        state.currentUser = payload as unknown as User;

        if (payload.refresh_token) storeRefreshToken(payload.refresh_token);
        if (payload.access_token) storeAccessToken(payload.access_token);

        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(signUpUser.rejected, (state: AuthState, action) => {
        state.loading = false;
        state.error = true;
        const payload = action.payload;
        if (payload && typeof payload === 'object' && 'errors' in payload) {
          state.errorMessages = (payload.errors as string[]) || [];
        } else {
          state.errorMessages = [];
        }
      })
      .addCase(loginUser.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(loginUser.fulfilled, (state: AuthState, action) => {
        const payload = action.payload;
        state.accessToken = payload.access_token;
        state.refreshToken = payload.refresh_token;
        state.expiresIn = payload.expires_in;
        state.currentUser = payload as unknown as User;
        state.currentCompany = payload.company;
        if (payload.refresh_token) storeRefreshToken(payload.refresh_token);
        if (payload.access_token) storeAccessToken(payload.access_token);

        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(loginUser.rejected, (state: AuthState) => {
        state.loading = false;
        state.error = true;
        state.errorMessages = [
          "Invalid credentials. Did you enter them correctly?",
        ];
      })
      .addCase(bootstrapSessionFromOAuthTokens.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(
        bootstrapSessionFromOAuthTokens.fulfilled,
        (state: AuthState, action) => {
          const payload = action.payload;
          state.accessToken = payload.access_token;
          state.refreshToken = payload.refresh_token;
          state.expiresIn = payload.expires_in;
          state.currentUser = payload as unknown as User;
          state.currentCompany = payload.company;
          if (payload.refresh_token) storeRefreshToken(payload.refresh_token);
          if (payload.access_token) storeAccessToken(payload.access_token);
          state.loading = false;
          state.error = false;
          state.errorMessages = [];
        }
      )
      .addCase(bootstrapSessionFromOAuthTokens.rejected, (state: AuthState) => {
        state.loading = false;
        state.error = true;
        state.errorMessages = [
          "Microsoft-innlogging fullførte ikke. Prøv igjen eller kontakt support.",
        ];
      })
      .addCase(syncSessionFromApi.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(syncSessionFromApi.fulfilled, (state: AuthState, action) => {
        const payload = action.payload;
        state.currentUser = payload as unknown as User;
        state.currentCompany = payload.company;
        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(syncSessionFromApi.rejected, (state: AuthState) => {
        state.loading = false;
      })
      .addCase(refreshAccessToken.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(
        refreshAccessToken.fulfilled,
        (state: AuthState, action) => {
          const payload = action.payload;
          state.accessToken = payload.access_token;
          state.refreshToken = payload.refresh_token;
          state.expiresIn = payload.expires_in;
          state.currentUser = payload as unknown as User;
          state.currentCompany = payload.company;

          if (payload.refresh_token) storeRefreshToken(payload.refresh_token);

          state.loading = false;
          state.error = false;
          state.errorMessages = [];
        }
      )
      .addCase(refreshAccessToken.rejected, (state: AuthState) => {
        state.loading = false;
        state.error = true;
      })
      .addCase(logoutUser.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(logoutUser.fulfilled, (state: AuthState) => {
        state.currentUser = {
          id: undefined,
          email: undefined,
          roles: undefined,
          createdAt: undefined,
        };
        state.currentCompany = {
          id: undefined,
          name: undefined,
          active_modules: undefined,
          hours_handler_id: undefined,
        };
        state.accessToken = undefined;
        state.refreshToken = undefined;
        state.expiresIn = undefined;
        state.tokenType = undefined;
        removeRefreshToken();
        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(logoutUser.rejected, (state: AuthState, action) => {
        state.loading = false;
        state.error = true;
        const payload = action.payload;
        if (payload && typeof payload === 'object' && 'error' in payload) {
          state.errorMessages = [payload.error as string];
        } else {
          state.errorMessages = [];
        }
      })
      .addCase(logoutUserWithoutToken.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(
        logoutUserWithoutToken.fulfilled,
        (state: AuthState) => {
          state.currentUser = {
            id: undefined,
            email: undefined,
            roles: undefined,
            createdAt: undefined,
          };
          state.currentCompany = {
            id: undefined,
            name: undefined,
            active_modules: undefined,
            hours_handler_id: undefined,
          };
          state.accessToken = undefined;
          state.refreshToken = undefined;
          state.expiresIn = undefined;
          state.tokenType = undefined;
          removeRefreshToken();
          removeAccessToken();
          state.loading = false;
          state.error = false;
          state.errorMessages = [];
        }
      )
      .addCase(
        logoutUserWithoutToken.rejected,
        (state: AuthState) => {
          state.loading = false;
          state.error = true;
          state.errorMessages = [];
        }
      )
      .addCase(updateProfile.pending, (state: AuthState) => {
        state.loading = true;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(updateProfile.fulfilled, (state: AuthState, action) => {
        const payload = action.payload;
        state.accessToken = payload.access_token;
        state.refreshToken = payload.refresh_token;
        state.expiresIn = payload.expires_in;
        state.tokenType = payload.token_type;
        state.currentUser = payload as unknown as User;
        state.currentCompany = payload.company;

        if (payload.refresh_token) storeRefreshToken(payload.refresh_token);

        state.loading = false;
        state.error = false;
        state.errorMessages = [];
      })
      .addCase(updateProfile.rejected, (state: AuthState, action) => {
        state.loading = false;
        state.error = true;
        const payload = action.payload;
        state.errorMessages = (payload?.errors as string[]) || [];
      });
  },
});

export const { resetErrorState } = sessionSlice.actions;
export const { setCurrentUser } = sessionSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.counter.value)`
// export const selectCount = (state: RootState) => state.counter.value;

// We can also write thunks by hand, which may contain both sync and async logic.
// Here's an example of conditionally dispatching actions based on current state.

export default sessionSlice.reducer;

function storeRefreshToken(token: string) {
  localStorage.setItem("refreshToken", token);
}

function storeAccessToken(token: string) {
  localStorage.setItem("accessToken", token);
}

function removeRefreshToken() {
  localStorage.removeItem("refreshToken");
}

function removeAccessToken() {
  localStorage.removeItem("accessToken");
}

export function getRefreshToken() {
  return localStorage.getItem("refreshToken");
}

export function getAccessToken() {
  return localStorage.getItem("accessToken");
}
